import { type ReactNode, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import FieldHint from "./FieldHint";
import GuideHelpButton from "./GuideHelpButton";
import type { ScheduleReadinessRow } from "../lib/api";
import { SETUP_MAX_STEP, setupGate } from "../lib/setupGates";

// «Checklist de categorías» compartido por los pasos 6-10 del asistente
// (Formato, Ranking, Grupos, Draw, Calendario). Antes solo vivía en el Draw.
// El nombre de cada categoría la selecciona (igual que el selector
// «Categoría seleccionada»: misma ruta con ?division=) y cada chip abre su
// sección. Se puede plegar; se recuerda en localStorage.

const OPEN_STORAGE_KEY = "t123.checklistCategorias.open";

function readOpen(): boolean {
  try {
    return window.localStorage.getItem(OPEN_STORAGE_KEY) !== "0";
  } catch {
    return true;
  }
}

function writeOpen(open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_STORAGE_KEY, open ? "1" : "0");
  } catch {
    // Sin almacenamiento: solo no se recuerda.
  }
}

interface Props {
  tournamentId: number;
  rows: ScheduleReadinessRow[];
  state: "loading" | "loaded" | "failed";
  /** Base de los links de los chips (checklistBasePath). */
  checklistBase: string;
  /** Asistente con torneo en borrador: el chip de Calendario respeta el candado del paso 10. */
  lockScheduleChip: boolean;
  usesCourtCalendar: boolean;
  selectedDivisionId: number | null;
  /** Aviso extra bajo el encabezado (p. ej. inscritos fuera del draw en el Draw). */
  alert?: ReactNode;
}

export default function DivisionChecklistCard({
  tournamentId,
  rows,
  state,
  checklistBase,
  lockScheduleChip,
  usesCourtCalendar,
  selectedDivisionId,
  alert,
}: Props) {
  const location = useLocation();
  const [open, setOpen] = useState<boolean>(readOpen);

  const toggle = () => {
    const next = !open;
    writeOpen(next);
    setOpen(next);
  };

  // Mientras el checklist carga por primera vez, la card reserva su lugar:
  // si aparece de golpe empuja los botones hacia abajo (QA Cowork #12).
  if (rows.length === 0) {
    if (state !== "loading") return null;
    return (
      <section className="card division-readiness" aria-busy="true">
        <div className="division-readiness__header">
          <h2>Checklist de categorías</h2>
          <GuideHelpButton />
          <p className="muted">Verificando formato, draw y calendario…</p>
        </div>
      </section>
    );
  }

  const scheduleChipGate = lockScheduleChip ? setupGate(rows) : null;
  const scheduleChipLocked = scheduleChipGate !== null && scheduleChipGate.maxStep < SETUP_MAX_STEP;

  const selectHref = (divisionId: number | null) => {
    const params = new URLSearchParams(location.search);
    if (divisionId) params.set("division", String(divisionId));
    else params.delete("division");
    const query = params.toString();
    return `${location.pathname}${query ? `?${query}` : ""}`;
  };

  return (
    <section className={`card division-readiness${open ? "" : " division-readiness--collapsed"}`}>
      <div className="division-readiness__header">
        <h2 className="hint-label">
          Checklist de categorías
          <FieldHint label="¿Qué valida el checklist?">
            Nada se juega sin formato, draw y calendario. Los pendientes se marcan en rojo.
          </FieldHint>
        </h2>
        <GuideHelpButton />
        <button
          type="button"
          className="btn btn--ghost btn--compact division-readiness__toggle"
          aria-expanded={open}
          onClick={toggle}
        >
          {open ? "Ocultar" : "Mostrar"}
        </button>
      </div>
      {open && alert}
      {open && (
        <div className="division-readiness__rows">
          {rows.map((row) => {
            const missingFormat = !row.format_saved;
            const missingDraw = row.participants > 0 && !row.has_draw;
            const staleDraw = (row.out_of_draw ?? 0) > 0;
            const divisionQuery = row.division_id ? `?division=${row.division_id}` : "";
            const selected = row.division_id !== null && row.division_id === selectedDivisionId;
            return (
              <div
                key={row.division_id ?? "torneo"}
                className={`division-readiness__row${missingFormat || missingDraw || staleDraw ? " division-readiness__row--alert" : ""}${selected ? " division-readiness__row--selected" : ""}`}
              >
                <Link
                  className="division-readiness__name"
                  to={selectHref(row.division_id)}
                  aria-current={selected ? "true" : undefined}
                >
                  {row.name}
                </Link>
                {missingFormat ? (
                  <Link
                    className="division-readiness__chip division-readiness__chip--bad"
                    to={`/admin/tournaments/${tournamentId}/setup/format${divisionQuery}`}
                  >
                    Sin formato → configurar
                  </Link>
                ) : (
                  <Link
                    className="division-readiness__chip division-readiness__chip--ok"
                    to={`/admin/tournaments/${tournamentId}/setup/format${divisionQuery}`}
                  >
                    Formato ✓
                  </Link>
                )}
                <Link
                  className={`division-readiness__chip${row.participants === 0 ? " division-readiness__chip--warn" : ""}`}
                  to={`${checklistBase}/registrations${divisionQuery}`}
                >
                  {row.participants} inscrito{row.participants === 1 ? "" : "s"}
                </Link>
                {missingDraw ? (
                  <Link
                    className="division-readiness__chip division-readiness__chip--bad"
                    to={`${checklistBase}/rounds${divisionQuery}`}
                  >
                    SIN DRAW → generar
                  </Link>
                ) : row.has_draw ? (
                  <Link
                    className="division-readiness__chip division-readiness__chip--ok"
                    to={`${checklistBase}/rounds${divisionQuery}`}
                  >
                    Draw ✓{row.format_type === "elimination" && !row.bracket_complete ? " · cuadro incompleto" : ""}
                  </Link>
                ) : (
                  <span className="division-readiness__chip">Sin jugadores aún</span>
                )}
                {(row.out_of_draw ?? 0) > 0 && (
                  <Link
                    className="division-readiness__chip division-readiness__chip--bad"
                    to={`${checklistBase}/rounds${divisionQuery}`}
                  >
                    Fuera del draw: {row.out_of_draw} → regenerar
                  </Link>
                )}
                {!usesCourtCalendar ? (
                  // Sin calendario de canchas (Eduardo 2026-10-05): no es un pendiente.
                  scheduleChipLocked ? (
                    <span className="division-readiness__chip" title={scheduleChipGate?.reason ?? undefined}>
                      🔒 Sin calendario de canchas
                    </span>
                  ) : (
                    <Link
                      className="division-readiness__chip"
                      to={`${checklistBase}/schedule${divisionQuery}`}
                      title="Los jugadores coordinan sus partidos entre ellos. Se cambia en Calendario."
                    >
                      Sin calendario de canchas
                    </Link>
                  )
                ) : scheduleChipLocked ? (
                  <span className="division-readiness__chip" title={scheduleChipGate?.reason ?? undefined}>
                    🔒 Calendario {row.scheduled}/{row.matches}
                  </span>
                ) : (
                  <Link
                    className={`division-readiness__chip${row.has_draw && row.scheduled === 0 ? " division-readiness__chip--warn" : ""}`}
                    to={`${checklistBase}/schedule${divisionQuery}`}
                  >
                    Calendario {row.scheduled}/{row.matches}
                  </Link>
                )}
                <Link
                  className="division-readiness__chip"
                  to={`/admin/tournaments/${tournamentId}/results${divisionQuery}`}
                >
                  Resultados {row.resolved}/{row.armed}
                </Link>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
